import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Image as ImgIcon, Plus, Sparkles, Calendar, Briefcase, Edit3, UserCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import StoryRail from '@/components/social/StoryRail';
import PostCard from '@/components/social/PostCard';
import CreatePostModal from '@/components/social/CreatePostModal';
import ProfileCompletion from '@/components/vendor/ProfileCompletion';
import FollowersModal from '@/components/social/FollowersModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveSrc = (src) => {
  if (!src) return null;
  if (src.startsWith('http')) return src;
  if (src.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
};

/**
 * Vendor Dashboard "Home" tab — Facebook-style social hub.
 *
 * Layout:
 *   1. Vendor profile header card (avatar + name + category + quick-edit) —
 *      mirrors the public vendor card so vendors recognize themselves.
 *   2. Story rail + "Make a Post" composer row — encourages fresh content.
 *   3. Their own post wall (most recent first).
 *
 * As a vendor scrolls, they see their own presence first and are nudged to
 * create, before they ever hit the metrics dashboard.
 */
export default function VendorHome() {
  const { user, getAuthHeaders } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({ followers_count: 0, following_count: 0, posts_count: 0 });
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [modalTab, setModalTab] = useState(null); // 'followers' | 'following' | null
  const postsRef = useRef(null);

  const load = async () => {
    try {
      const [vRes, sRes, pRes, cRes] = await Promise.all([
        fetch(`${API}/vendors/me`, { headers: getAuthHeaders() }),
        fetch(`${API}/social/users/${user?.user_id}/social`, { headers: getAuthHeaders() }),
        fetch(`${API}/social/users/${user?.user_id}/posts?limit=20`, { headers: getAuthHeaders() }),
        fetch(`${API}/categories`),
      ]);
      if (vRes.ok) setVendor(await vRes.json());
      if (sRes.ok) setStats(await sRes.json());
      if (pRes.ok) setPosts((await pRes.json()).posts || []);
      if (cRes.ok) {
        const cats = await cRes.json();
        const map = {};
        (Array.isArray(cats) ? cats : cats.categories || []).forEach((c) => {
          map[c.category_id || c.id] = c.name;
        });
        setCategories(map);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.user_id) load(); /* eslint-disable-next-line */ }, [user?.user_id]);

  const handleCreated = () => { setShowCreate(false); load(); };

  const avatar = resolveSrc(vendor?.profile_image || user?.picture);
  const name = vendor?.business_name || vendor?.owner_name || user?.name;

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="vendor-home-tab">
      {/* Profile Completion CTA (moved here from Overview) */}
      <ProfileCompletion />

      {/* 1 — Profile hero card */}
      <div
        className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#13153A]/70 backdrop-blur-md shadow-[0_12px_40px_-18px_rgba(139,92,246,0.5)]"
        data-testid="vendor-home-profile-card"
      >
        <div
          className="h-32 sm:h-40 w-full bg-[#1A1A3E]"
          style={{
            backgroundImage: vendor?.banner_image
              ? `url(${resolveSrc(vendor.banner_image)})`
              : 'linear-gradient(135deg, #6B2DFF 0%, #3B82F6 50%, #06B6D4 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="px-4 sm:px-6 pb-5 pt-4 relative">
          {/* Avatar — the ONLY thing that overlaps the banner */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border-4 border-[#0B0D2B] bg-[#2D2D52]/60 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.6)] -mt-14 sm:-mt-16 mb-3">
            {avatar ? (
              <img src={avatar} alt={name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-fuchsia-200 text-2xl font-bold">
                {(name || '?')[0]}
              </div>
            )}
          </div>

          {/* Name + subtitle + description — below the banner, inside the card */}
          <h2 className="text-white font-syne text-lg sm:text-2xl font-bold">{name || 'Your business'}</h2>
          <p className="text-zinc-400 text-xs sm:text-sm mt-0.5">
            {(() => {
              const parts = [];
              if (vendor?.category_id && categories[vendor.category_id]) parts.push(categories[vendor.category_id]);
              if (vendor?.owner_name && vendor?.business_name && vendor.owner_name !== vendor.business_name) {
                parts.push(`by ${vendor.owner_name}`);
              }
              if (vendor?.location) parts.push(vendor.location);
              return parts.filter(Boolean).join(' · ') || 'Complete your profile to appear in search';
            })()}
          </p>
          {vendor?.description && (
            <p className="text-zinc-300 text-xs sm:text-sm mt-1.5 line-clamp-2">{vendor.description}</p>
          )}

          {/* Social stats row — clickable Followers / Following open the list modal */}
          <div className="flex items-center gap-4 mt-4 text-xs">
            <span className="text-white" data-testid="vendor-home-posts">
              <b className="font-bold">{stats.posts_count}</b> <span className="text-zinc-400">posts</span>
            </span>
            <button
              type="button"
              onClick={() => setModalTab('followers')}
              className="text-white inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-white/5 active:bg-white/10"
              data-testid="vendor-home-followers"
            >
              <b className="font-bold">{stats.followers_count}</b>
              <span className="text-zinc-400">followers</span>
            </button>
            <button
              type="button"
              onClick={() => setModalTab('following')}
              className="text-white inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-white/5 active:bg-white/10"
              data-testid="vendor-home-following"
            >
              <b className="font-bold">{stats.following_count}</b>
              <span className="text-zinc-400">following</span>
            </button>
          </div>

          {/* Actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            {vendor?.vendor_id && (
              <Link
                to={`/vendor/${vendor.vendor_id}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 hover:border-fuchsia-400/40"
                data-testid="vendor-home-view-public"
              >
                <UserCheck size={12} /> View as public
              </Link>
            )}
            <Link
              to="/dashboard/vendor/profile"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 hover:border-fuchsia-400/40"
              data-testid="vendor-home-edit"
            >
              <Edit3 size={12} /> Edit profile
            </Link>
            <Link
              to="/dashboard/vendor/services"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 hover:border-fuchsia-400/40"
              data-testid="vendor-home-services"
            >
              <Briefcase size={12} /> Services
            </Link>
            <Link
              to="/events"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 hover:border-fuchsia-400/40"
              data-testid="vendor-home-events"
            >
              <Calendar size={12} /> Events
            </Link>
          </div>
        </div>
      </div>

      {/* 2 — Story rail + composer nudge */}
      <StoryRail viewer={user} canPost />

      <button
        type="button"
        onClick={() => setShowCreate(true)}
        className="w-full flex items-center gap-3 rounded-2xl bg-[#13153A]/70 border border-white/10 hover:border-fuchsia-400/60 transition-all p-3 text-left"
        data-testid="vendor-home-make-post"
      >
        <span className="relative w-14 h-14 rounded-full bg-gradient-to-br from-fuchsia-500 via-pink-500 to-amber-400 flex items-center justify-center shadow-[0_0_22px_rgba(236,72,153,0.35)] flex-shrink-0">
          <span className="absolute inset-[3px] rounded-full bg-[#0B0D2B] flex items-center justify-center">
            <Plus size={22} strokeWidth={2.5} className="text-white" />
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-white font-bold text-sm font-syne">Share something with EntrepreNatives</span>
          <span className="block text-zinc-400 text-[11px]">A photo, a story, an update — take 30 seconds.</span>
        </span>
        <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-fuchsia-500/15 text-fuchsia-200 text-[10px] font-bold border border-fuchsia-400/30">
          <Sparkles size={10} /> Post
        </span>
      </button>

      {/* 3 — Your posts feed */}
      <div ref={postsRef}>
        <div className="flex items-center justify-between mb-3 mt-2">
          <h3 className="text-white font-syne font-bold text-lg flex items-center gap-2">
            <ImgIcon size={16} className="text-fuchsia-300" /> Your posts
          </h3>
          <Link to="/feed" className="text-xs text-zinc-400 hover:text-fuchsia-300" data-testid="vendor-home-open-feed">See everyone's →</Link>
        </div>
        {loading ? (
          <div className="rounded-2xl border border-white/10 p-8 text-center text-zinc-500 text-sm">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center" data-testid="vendor-home-empty-posts">
            <p className="text-white font-semibold text-sm">No posts yet</p>
            <p className="text-zinc-400 text-xs mt-1">Tap "Share something" above to make your first one.</p>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-bold"
              data-testid="vendor-home-empty-cta"
            >
              <Plus size={14} /> Create first post
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            {posts.map((p) => (
              <PostCard key={p.post_id} post={p} viewer={user} onChanged={load} />
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreatePostModal
          open
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          viewer={user}
        />
      )}
    </div>
  );
}
